import {
  Newspaper,
  Pause,
  Play,
  Settings,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { SettingsPage } from "./SettingsPage";
import { StoryDetail } from "./StoryDetail";
import { StoryList } from "./StoryList";
import {
  loadSettings,
  loadSources,
  loadStories,
  loadUsage,
  runResearch,
  setAutomationEnabled,
} from "@/lib/newsroom";
import { supabase } from "@/lib/supabase";
import type {
  NewsroomSettings,
  NewsSource,
  ProviderUsage,
  Story,
  StoryStatus,
} from "@/lib/types";

type Panel = "inbox" | "settings";

const filters: Array<{ value: StoryStatus | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "ready", label: "Ready" },
  { value: "needs_review", label: "Review" },
  { value: "conflict", label: "Conflicts" },
];

export function Newsroom({ ownerId }: { ownerId: string }) {
  const initialStoryId = new URLSearchParams(window.location.search).get("story");
  const [panel, setPanel] = useState<Panel>("inbox");
  const [filter, setFilter] = useState<StoryStatus | "all">("all");
  const [stories, setStories] = useState<Story[]>([]);
  const [sources, setSources] = useState<NewsSource[]>([]);
  const [settings, setSettings] = useState<NewsroomSettings | null>(null);
  const [usage, setUsage] = useState<ProviderUsage[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(initialStoryId);
  const [automationBusy, setAutomationBusy] = useState(false);
  const [runMessage, setRunMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [bundleVersion, setBundleVersion] = useState(0);

  const refreshStories = useCallback(async () => {
    const nextStories = await loadStories(filter);
    setStories(nextStories);
    setSelectedId((current) => {
      if (current && nextStories.some((story) => story.id === current)) {
        return current;
      }
      return nextStories[0]?.id ?? null;
    });
  }, [filter]);

  const refreshSources = useCallback(async () => {
    setSources(await loadSources());
  }, []);

  const refreshSettings = useCallback(async () => {
    const [nextSettings, nextUsage] = await Promise.all([
      loadSettings(),
      loadUsage(),
    ]);
    setSettings(nextSettings);
    setUsage(nextUsage);
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([refreshStories(), refreshSources(), refreshSettings()])
      .catch((error: unknown) => {
        setRunMessage(
          error instanceof Error ? error.message : "Unable to load the newsroom",
        );
      })
      .finally(() => setLoading(false));
  }, [refreshSettings, refreshSources, refreshStories]);

  useEffect(() => {
    const channel = supabase
      .channel(`newsroom-${ownerId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "story_clusters",
          filter: `owner_id=eq.${ownerId}`,
        },
        () => {
          void refreshStories();
          setBundleVersion((current) => current + 1);
        },
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "drafts",
          filter: `owner_id=eq.${ownerId}`,
        },
        () => setBundleVersion((current) => current + 1),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [ownerId, refreshStories]);

  const selectedStory =
    stories.find((story) => story.id === selectedId) ?? null;

  async function toggleAutomation() {
    if (!settings) {
      return;
    }
    const enabled = !settings.automation_enabled;
    setAutomationBusy(true);
    setRunMessage("");
    try {
      await setAutomationEnabled(ownerId, enabled);
      setSettings({ ...settings, automation_enabled: enabled });
      if (!enabled) {
        setRunMessage("Automation paused");
        return;
      }
      try {
        const result = await runResearch(true);
        setRunMessage(
          `${result.accepted} new · ${result.ready} ready · next run in 1 min`,
        );
        await Promise.all([
          refreshStories(),
          refreshSources(),
          refreshSettings(),
        ]);
      } catch (error) {
        setRunMessage(
          `Automation is on · ${
            error instanceof Error ? error.message : "initial run failed"
          }`,
        );
      }
    } catch (error) {
      setRunMessage(
        error instanceof Error ? error.message : "Unable to update automation",
      );
    } finally {
      setAutomationBusy(false);
    }
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand-lockup">NEWSROOM</div>
        <div className="topbar-actions">
          <span className="run-message">{runMessage}</span>
          <span
            className={`automation-status ${
              settings?.automation_enabled ? "is-live" : ""
            }`}
          >
            {settings?.automation_enabled ? "LIVE · 1 MIN" : "PAUSED"}
          </span>
          <button
            type="button"
            className={`automation-button ${
              settings?.automation_enabled ? "is-active" : ""
            }`}
            aria-label={
              settings?.automation_enabled
                ? "Pause scheduled research"
                : "Start scheduled research every minute"
            }
            title={
              settings?.automation_enabled
                ? "Pause scheduled research"
                : "Start scheduled research every minute"
            }
            onClick={() => void toggleAutomation()}
            disabled={!settings || automationBusy}
          >
            {settings?.automation_enabled ? (
              <Pause aria-hidden="true" />
            ) : (
              <Play aria-hidden="true" />
            )}
          </button>
        </div>
      </header>

      <div className="app-body">
        <aside className="sidebar">
          <nav>
            <button
              type="button"
              className={panel === "inbox" ? "is-active" : ""}
              aria-label={`Stories, ${stories.length} total`}
              title={`${stories.length} stories`}
              onClick={() => setPanel("inbox")}
            >
              <Newspaper aria-hidden="true" />
            </button>
          </nav>
          <div className="sidebar-bottom">
            <button
              type="button"
              className={panel === "settings" ? "is-active" : ""}
              aria-label="Settings"
              title="Settings"
              onClick={() => setPanel("settings")}
            >
              <Settings aria-hidden="true" />
            </button>
          </div>
        </aside>

        <section className="workspace">
          {panel === "inbox" ? (
            <>
              <section className="inbox-column">
                <div className="inbox-toolbar">
                  <h1>Stories</h1>
                </div>
                <div className="filter-tabs">
                  {filters.map((item) => (
                    <button
                      type="button"
                      key={item.value}
                      className={filter === item.value ? "is-active" : ""}
                      onClick={() => setFilter(item.value)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                {loading ? (
                  <div className="loading-list">
                    <span />
                    <span />
                    <span />
                  </div>
                ) : (
                  <StoryList
                    stories={stories}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                  />
                )}
              </section>
              <section className="detail-column">
                <StoryDetail
                  story={selectedStory}
                  settings={settings}
                  bundleVersion={bundleVersion}
                  onChanged={refreshStories}
                />
              </section>
            </>
          ) : null}
          {panel === "settings" && settings ? (
            <SettingsPage
              ownerId={ownerId}
              settings={settings}
              sources={sources}
              usage={usage}
              onRefreshSettings={refreshSettings}
              onRefreshSources={refreshSources}
            />
          ) : null}
          {panel === "settings" && !settings ? (
            <div className="detail-empty">
              <Settings aria-hidden="true" />
              <h2>Settings are still loading.</h2>
            </div>
          ) : null}
        </section>
      </div>
    </main>
  );
}
